import { Menu } from "@mantine/core"
import { modals } from "@mantine/modals"
import { IconReportOff } from "@tabler/icons-react"
import type { PropsWithChildren } from "react"
import { useTranslation } from "react-i18next"
import { useAPIResource, useConfirm, usePermissions } from "@/hooks"
import { checkSurvey } from "@/types"
import type { TSession } from "@/types/activities"
import { sessionsAPI } from "@/utils"
import { isOver, isStarted } from "@/utils/session"
import { useScheduleSession } from "../activities"
import { ActionsMenu } from "../controls"
import { AppTitle, icons } from "../utils"
import { SessionEdit } from "./SessionEdit"

interface IProps {
  session: TSession
}

export function SessionMenu({ session, children }: PropsWithChildren<IProps>) {
  const { t } = useTranslation()
  const { isAdmin, user } = usePermissions()
  const hasPermissions = isAdmin || session.user_id === user?.id
  const canRepeat = hasPermissions && isOver(session) && !["canceled", "declined"].includes(session.state)

  const { remove, update } = useAPIResource(sessionsAPI)

  const confirm = useConfirm({
    labels: { confirm: t("Cancel"), cancel: t("Keep") },
    title: t("Do you want to cancel this session?"),
  })
  const { open, openRepeat } = useScheduleSession(session)

  return (
    <ActionsMenu>
      {checkSurvey(session) && (
        <Menu.Item
          onClick={() =>
            modals.openContextModal({
              size: "xl",
              modal: "ActivityInfo",
              title: <AppTitle>{session.title}</AppTitle>,
              innerProps: { activityId: session.activity_id },
            })
          }
          leftSection={<icons.Info size={14} />}
        >
          {t("Details")}
        </Menu.Item>
      )}
      {children}
      {hasPermissions && isStarted(session) ? (
        <Menu.Item
          onClick={() =>
            modals.open({
              title: <AppTitle>{t("Settings")}</AppTitle>,
              children: <SessionEdit session={session} />,
            })
          }
          leftSection={<icons.Edit size={14} />}
        >
          {t("Change")}
        </Menu.Item>
      ) : (
        <Menu.Item onClick={() => open()} leftSection={<icons.Calendar size={14} />}>
          {t("Reschedule")}
        </Menu.Item>
      )}
      {canRepeat && (
        <Menu.Item onClick={openRepeat} leftSection={<icons.Recurrence size={14} />}>
          {t("Repeat")}
        </Menu.Item>
      )}
      {hasPermissions &&
        (isOver(session) ? (
          <Menu.Item
            color="red"
            leftSection={<IconReportOff size={14} />}
            onClick={() => {
              update({ id: session.id, visibility: "nobody" })
            }}
          >
            {t("Hide")}
          </Menu.Item>
        ) : (
          <Menu.Item
            color="red"
            leftSection={<icons.Remove size={14} />}
            onClick={async () => {
              if (await confirm()) remove(session.id)
            }}
          >
            {t("Cancel")}
          </Menu.Item>
        ))}
    </ActionsMenu>
  )
}
